import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import React from 'react';
import PropTypes from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/fieldset.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import ReactUtils from '../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import { InnerRefPropType } from '../../packages/props-utils/prop-types.js';
import { InputInfoChildren, InputInfo } from '../_atoms/input-info/input-info.js';
import useComponentId from '../../hooks/use-component-id/use-component-id.js';
import { SPACE_VALUES } from '../../helpers/types.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const bem = new HoneyBem('fieldset', styles);
function Fieldset(_a) {
  var {
      id,
      children,
      extraClasses = '',
      horizontal = false,
      valid = null,
      innerRef,
      info = null,
      infoError = null,
      itemSpacing = '2'
    } = _a,
    rest = __rest(_a, ["id", "children", "extraClasses", "horizontal", "valid", "innerRef", "info", "infoError", "itemSpacing"]);
  const childrenArray = ReactUtils.mapRecursively(children, child => child);
  /**
   * Generates a dynamic component ID in the following format:
   * N * child.id[first-letter]+[last-letter]
   * This is needed for info elements and to bind them to the form controls
   */
  const idPrefix = React.useMemo(() => childrenArray.map(child => {
    const {
      id: childID
    } = /*#__PURE__*/React.isValidElement(child) && child.props;
    return childID && "".concat(childID.charAt(0)).concat(childID.charAt(childID.length - 1));
  }).join(''), [childrenArray]);
  const componentId = useComponentId(idPrefix, id);
  const legend = childrenArray.filter(child => ReactUtils.isOfType(child, 'Legend'));
  const controls = childrenArray.filter(child => !ReactUtils.isOfType(child, 'Legend'));
  const infoErrorId = valid === false ? infoError && "infoError-".concat(componentId) : null;
  const infoId = info ? "info-".concat(componentId) : null;
  // everything apart from a Legend component gets wrapped into fieldset item container
  const applyWrapperAndAttrs = (child, index) => {
    const arrDescribedBy = [];
    const childAriaDescribedBy = child === null || child === void 0 ? void 0 : child.props['aria-describedby'];
    const spacing = index > 0 && "space-".concat(itemSpacing);
    if (infoId) arrDescribedBy.push(infoId);
    if (infoErrorId) arrDescribedBy.push(infoErrorId);
    if (childAriaDescribedBy) arrDescribedBy.push(childAriaDescribedBy);
    return /*#__PURE__*/React.createElement("div", {
      key: child && child.key,
      className: bem.e('item', spacing)
    }, /*#__PURE__*/React.cloneElement(child, Object.assign(Object.assign({}, arrDescribedBy.length > 0 && {
      'aria-describedby': arrDescribedBy.join(' ')
    }), infoError && {
      'aria-errormessage': "infoError-".concat(componentId)
    })));
  };
  return /*#__PURE__*/React.createElement("fieldset", Object.assign({
    id: id,
    ref: innerRef,
    className: bem.b({
      error: valid === false,
      horizontal
    }, extraClasses)
  }, rest), legend, ReactUtils.mapRecursively(controls, applyWrapperAndAttrs), valid === false && infoError && (/*#__PURE__*/React.createElement(InputInfo, {
    error: true,
    id: componentId,
    className: bem.e('info')
  }, infoError)), info && (/*#__PURE__*/React.createElement(InputInfo, {
    id: componentId,
    className: bem.e('info')
  }, info)));
}
Fieldset.displayName = 'Fieldset';
Fieldset.propTypes = {
  id: PropTypes.string,
  extraClasses: PropTypes.string,
  children: PropTypes.node.isRequired,
  horizontal: PropTypes.bool,
  valid: PropTypes.bool,
  innerRef: InnerRefPropType,
  info: InputInfoChildren,
  infoError: InputInfoChildren,
  itemSpacing: PropTypes.oneOf(SPACE_VALUES)
};
var fieldset = Injecss(Fieldset, styles);

export { Fieldset, fieldset as default };
